import React from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Box, LinearProgress } from '@mui/material';
import { useMutation } from 'react-fetching-library';

import ContextMenu from './context-menu/ContextMenu';
import ContextMenuModal from './context-menu/ContextMenuModal';

import { plPL } from '@mui/x-data-grid';
import { DataGridToolbar } from '../../../components/data-grid/DataGridToolbar';
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef
} from '@mui/x-data-grid-pro';

import OrderDetails from './OrderDetails';
import { getOrderColumns } from '../../../services/functions';
import {
  setOrder,
  setAllowAutoOrderUpdate,
  setOLOrderHiddenColumns,
  setZSOrderHiddenColumns,
  setPLOrderHiddenColumns,
  setWROrderHiddenColumns,
  setOLOrderColumnsWidth,
  setZSOrderColumnsWidth,
  setPLOrderColumnsWidth,
  setWROrderColumnsWidth,
  setOLOrderColumnsSequence,
  setZSOrderColumnsSequence,
  setPLOrderColumnsSequence,
  setWROrderColumnsSequence,
  setExternalOrderId
} from '../../../services/redux-actions';
import {
  updateEmployeeConfigAction,
  getOrderDetailsAction
} from '../../../services/request';
import {
  useDidMountEffect,
  useGetSeasonConfig,
  useResponsive
} from '../../../services/hooks';
// ----------------------------------------------------------------------

const RootStyle = styled('div')({
  flexGrow: 1,
  display: 'flex',
  overflow: 'hidden',
  flexDirection: 'column'
});

// ----------------------------------------------------------------------

export default function OrderList({
  onOpenSidebar,
  hideSidebar,
  count,
  queryCallback,
  loading,
  orders,
  updateOrders,
  removeOrderFromOrders,
  addOrder,
  refreshOrders,
  handleAccomodationCheck,
  handleConfirmStatus,
  updateSingleRowData,
  selectedOrdersIds,
  setSelectedOrdersIds
}) {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const getOrderDataMutation = useMutation(getOrderDetailsAction);
  const { mutate } = useMutation(updateEmployeeConfigAction);
  const { employeeId, employeeConfigId, module, season } = useSelector(
    state => state.app
  );
  const { externalOrderId } = useSelector(state => state.orderData);
  const { orderHiddenColumns, orderColumnsWidth, orderColumnsSequence } =
    useGetSeasonConfig(module, season);
  const [openCompose, setOpenCompose] = React.useState(false);
  const [fullScreen, setFullScreen] = React.useState(false);
  const [hiddenColumns, setHiddenColumns] = React.useState(
    _.isNil(orderHiddenColumns) ? [] : orderHiddenColumns
  );
  const [rowsState, setRowsState] = React.useState({
    page: 0,
    pageSize: 100,
    rows: orders,
    loading: false,
    sortFields: ['-id'],
    search: undefined
  });
  const [update, setUpdate] = React.useState(true);

  const [selectedOrder, setSelectedOrder] = React.useState({});
  const [contextMenu, setContextMenu] = React.useState(null);
  const [openState, setOpenState] = React.useState(false);
  const [modalType, setModalType] = React.useState('');
  const [openModal, setOpenModal] = React.useState(false);
  const isDesktop = useResponsive('up', 'lg');

  const handleOpenModal = type => {
    setModalType(type);
    setOpenModal(true);
    handleClose();
    dispatch(setAllowAutoOrderUpdate(false));
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    dispatch(setAllowAutoOrderUpdate(true));
    if (modalType === 'changeState') {
      setModalType('');
    }
    refreshOrders();
  };

  const handleContextMenu = event => {
    event.preventDefault();
    const orderId = Number(event.currentTarget.getAttribute('data-id'));
    const order = orders.find(order => order.id === orderId);
    setSelectedOrder(order);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    );
    dispatch(setAllowAutoOrderUpdate(false));
  };

  const handleClose = () => {
    setContextMenu(null);
    setOpenState(false);
    dispatch(setAllowAutoOrderUpdate(true));
  };

  const handleDetailsCheck = async (data, doubleClick = false) => {
    const { error, payload } = await getOrderDataMutation.mutate(data.id);
    if (!error) {
      if (doubleClick) {
        setFullScreen(true);
      }
      dispatch(setOrder(payload));
      dispatch(setAllowAutoOrderUpdate(false));
      dispatch(setExternalOrderId(null));
      setOpenCompose(true);
    }
  };
  const [columns, setColumns] = React.useState(
    getOrderColumns(
      module,
      handleDetailsCheck,
      handleAccomodationCheck,
      handleConfirmStatus
    )
  );

  React.useEffect(() => {
    if (update) {
      const columnsClone = getOrderColumns(
        module,
        handleDetailsCheck,
        handleAccomodationCheck,
        handleConfirmStatus
      );
      const idxs = [];
      _.forEach(orderHiddenColumns, item => {
        const idx = columnsClone.findIndex(obj => obj.field == item);
        if (idx > -1) {
          idxs.push(idx);
          columnsClone[idx].hide = true;
        }
      });
      _.map(columnsClone, (obj, idx) => {
        if (!idxs.includes(idx)) {
          columnsClone[idx].hide = false;
        }
        if (!_.isNil(orderColumnsWidth) && obj.field in orderColumnsWidth) {
          columnsClone[idx].width = orderColumnsWidth[obj.field];
        }
      });
      _.map(orderColumnsSequence, (item, itemIdx) => {
        const itemIdxInt = parseInt(itemIdx);
        const idx = _.findIndex(columnsClone, obj => obj.field == item);
        if (idx != itemIdxInt && idx > -1) {
          [columnsClone[idx], columnsClone[itemIdxInt]] = [
            columnsClone[itemIdxInt],
            columnsClone[idx]
          ];
        }
      });
      setColumns(columnsClone);
      setHiddenColumns(_.isNil(orderHiddenColumns) ? [] : orderHiddenColumns);
    } else {
      setUpdate(true);
    }
  }, [orderHiddenColumns, orderColumnsWidth, orderColumnsSequence]);

  React.useEffect(() => {
    setRowsState({ ...rowsState, rows: orders });
  }, [orders]);

  const setModuleHiddenColumns = hiddenColumnsClone => {
    if (module === 'obozy') {
      return setOLOrderHiddenColumns({ [season]: hiddenColumnsClone });
    } else if (module === 'zielone_szkoly') {
      return setZSOrderHiddenColumns({ [season]: hiddenColumnsClone });
    } else if (module === 'polkolonie') {
      return setPLOrderHiddenColumns({ [season]: hiddenColumnsClone });
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderHiddenColumns({ [season]: hiddenColumnsClone });
    }
  };

  const moduleHiddenColumnsMutationBody = payload => {
    if (module === 'obozy') {
      return { ol_order_hidden_columns: payload, employee: employeeId };
    } else if (module === 'zielone_szkoly') {
      return { zs_order_hidden_columns: payload, employee: employeeId };
    } else if (module === 'polkolonie') {
      return { pl_order_hidden_columns: payload, employee: employeeId };
    } else if (module === 'wyjazdy_rodzinne') {
      return { wr_order_hidden_columns: payload, employee: employeeId };
    }
  };

  React.useEffect(() => {
    queryCallback(
      rowsState.page,
      rowsState.pageSize,
      rowsState.search,
      rowsState.sortFields
    );
    setRowsState(prev => ({ ...prev, loading: false }));
  }, [rowsState.page, rowsState.pageSize, rowsState.sortFields]);

  React.useEffect(() => {
    if (externalOrderId) {
      handleDetailsCheck({ id: externalOrderId }, true);
    }
  }, [externalOrderId]);

  const handleColumnVisibilityChange = async col => {
    const columnsClone = _.cloneDeep(columns);
    const idx = _.findIndex(columns, obj => obj.field == col.field);
    columnsClone[idx].hide = !columnsClone[idx].hide;
    setColumns(columnsClone);
    const hiddenColumnsClone = [...hiddenColumns];
    const hiddenIdx = hiddenColumnsClone.indexOf(col.field);
    setUpdate(false);
    if (hiddenIdx > -1) {
      hiddenColumnsClone.splice(hiddenIdx, 1);
      setHiddenColumns(hiddenColumnsClone);
      const { payload } = dispatch(setModuleHiddenColumns(hiddenColumnsClone));
      await mutate(moduleHiddenColumnsMutationBody(payload), employeeConfigId);
    } else {
      setHiddenColumns([col.field, ...hiddenColumns]);
      const { payload } = dispatch(
        setModuleHiddenColumns([col.field, ...hiddenColumns])
      );
      await mutate(moduleHiddenColumnsMutationBody(payload), employeeConfigId);
    }
  };

  const searchCallback = search => {
    setRowsState({ ...rowsState, search });
    queryCallback(0, rowsState.pageSize, search, rowsState.sortFields);
  };

  const setModuleColumnsWidth = columnsWidth => {
    if (module === 'obozy') {
      return setOLOrderColumnsWidth({ [season]: columnsWidth });
    } else if (module === 'zielone_szkoly') {
      return setZSOrderColumnsWidth({ [season]: columnsWidth });
    } else if (module === 'polkolonie') {
      return setPLOrderColumnsWidth({ [season]: columnsWidth });
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderColumnsWidth({ [season]: columnsWidth });
    }
  };

  const moduleColumnsWidthMutationBody = payload => {
    if (module === 'obozy') {
      return { ol_order_columns_width: payload, employee: employeeId };
    } else if (module === 'zielone_szkoly') {
      return { zs_order_columns_width: payload, employee: employeeId };
    } else if (module === 'polkolonie') {
      return { pl_order_columns_width: payload, employee: employeeId };
    } else if (module === 'wyjazdy_rodzinne') {
      return { wr_order_columns_width: payload, employee: employeeId };
    }
  };

  const handleColumnResizeChange = async colDef => {
    const { payload } = dispatch(
      setModuleColumnsWidth({
        ...orderColumnsWidth,
        [colDef.field]: colDef.width
      })
    );
    await mutate(moduleColumnsWidthMutationBody(payload), employeeConfigId);
  };

  const setModuleColumnsSequence = columnsSequence => {
    if (module === 'obozy') {
      return setOLOrderColumnsSequence({ [season]: columnsSequence });
    } else if (module === 'zielone_szkoly') {
      return setZSOrderColumnsSequence({ [season]: columnsSequence });
    } else if (module === 'polkolonie') {
      return setPLOrderColumnsSequence({ [season]: columnsSequence });
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderColumnsSequence({ [season]: columnsSequence });
    }
  };

  const moduleColumnsSequenceMutationBody = payload => {
    if (module === 'obozy') {
      return { ol_order_columns_sequence: payload, employee: employeeId };
    } else if (module === 'zielone_szkoly') {
      return { zs_order_columns_sequence: payload, employee: employeeId };
    } else if (module === 'polkolonie') {
      return { pl_order_columns_sequence: payload, employee: employeeId };
    } else if (module === 'wyjazdy_rodzinne') {
      return { wr_order_columns_sequence: payload, employee: employeeId };
    }
  };

  const handleColumnsOrderChange = async e => {
    const columnOrder = apiRef.current
      .getAllColumns()
      .map(col => col.field)
      .filter(col => col !== '__check__');
    const { payload } = dispatch(setModuleColumnsSequence(columnOrder));
    await mutate(moduleColumnsSequenceMutationBody(payload), employeeConfigId);
  };

  const handleOnCellClick = params => {
    if (params.field === 'Szczegóły') {
      return;
    }
    handleDetailsCheck(params.row);
  };

  const handleSortModelChange = sortModel => {
    let sortingArr = [];
    for (const item of sortModel) {
      if (item.sort == 'asc') {
        if (item.field == 'identifier') {
          sortingArr.push('season_number');
        } else {
          sortingArr.push(item.field);
        }
      } else if (item.sort == 'desc') {
        if (item.field == 'identifier') {
          sortingArr.push('-season_number');
        } else {
          sortingArr.push(`-${item.field}`);
        }
      }
    }
    if (_.isEmpty(sortingArr)) {
      sortingArr = ['-id'];
    }
    setRowsState({ ...rowsState, sortFields: sortingArr });
  };

  useDidMountEffect(() => {
    if (!_.isEmpty(updateSingleRowData)) {
      console.log(rowsState);
      queryCallback(
        rowsState.page,
        rowsState.pageSize,
        rowsState.search,
        rowsState.sortFields
      );
    }
  }, [updateSingleRowData]);

  return (
    <RootStyle>
      <OrderDetails
        updateOrders={updateOrders}
        isOpenCompose={openCompose}
        parentFullScreen={fullScreen}
        refreshOrders={refreshOrders}
        setSelectionModel={setSelectedOrdersIds}
        onCloseCompose={() => {
          setOpenCompose(false);
          setFullScreen(false);
        }}
      />
      <Box
        sx={
          isDesktop
            ? { minWidth: { md: 800 }, height: '100%' }
            : { width: '100%', height: '80vh' }
        }
      >
        <DataGridPro
          apiRef={apiRef}
          onColumnResize={e => handleColumnResizeChange(e.colDef)}
          onColumnOrderChange={e => handleColumnsOrderChange(e)}
          columns={columns ? columns : []}
          rows={orders}
          density="compact"
          disableColumnMenu={true}
          autoPageSize={false}
          rowsPerPageOptions={[10, 25, 50, 100, 250]}
          components={{
            Toolbar: DataGridToolbar,
            LoadingOverlay: () => <LinearProgress sx={{ zIndex: 2 }} />
          }}
          componentsProps={{
            toolbar: {
              onOpenSidebar,
              hideSidebar,
              searchCallback,
              showAll: true,
              advancedFilter: true,
              exportToolbar: false
            },
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: 'context-menu' }
            }
          }}
          initialState={{
            pinnedColumns: {
              left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'Szczegóły']
            }
          }}
          localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
          checkboxSelection
          disableSelectionOnClick
          // onRowClick={params => handleDetailsCheck(params.row)}
          onCellClick={handleOnCellClick}
          onRowDoubleClick={params => handleDetailsCheck(params.row, true)}
          onColumnVisibilityChange={col => handleColumnVisibilityChange(col)}
          pagination
          rowCount={count}
          {...rowsState}
          loading={loading}
          paginationMode="server"
          onPageChange={page => setRowsState(prev => ({ ...prev, page }))}
          onPageSizeChange={pageSize => setRowsState(prev => ({ ...prev, pageSize }))
          }
          onSelectionModelChange={newSelectionModel => {
            setSelectedOrdersIds(newSelectionModel);
          }}
          selectionModel={selectedOrdersIds}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
        />
        <ContextMenu
          contextMenu={contextMenu}
          handleClose={handleClose}
          handleOpenModal={handleOpenModal}
        />
      </Box>
      <ContextMenuModal
        type={modalType}
        order={selectedOrder}
        open={openModal}
        handleClose={handleCloseModal}
        removeOrderFromOrders={removeOrderFromOrders}
        addOrder={addOrder}
      />
    </RootStyle>
  );
}
